/**
 * Why a ctx load happened (TR-5544). Every load sends its trigger as `?op=ctx&trigger=<name>`, so the
 * Traefik access log can split ctx traffic by cause. Each name belongs to exactly one place in the code.
 */
export const CTX_LOAD_TRIGGERS = [
  // Lifecycle
  "route", // first load of a server, or switching to another one
  "auth", // the route watcher ran again because the authentication state changed
  "retry", // handleCtxLoadError's backoff timer
  "visible_again", // a hidden tab was shown again after reloads were deferred
  // Automatic, in every open tab of the server
  "transition_end", // the service's FSM transition finished
  "event_backup_user", // backup_created, customer backup
  "event_backup_auto", // backup_created, automatic backup
  "event_backup_other", // backup_created, any other backup type
  // Customer actions
  "config_save",
  "auto_update_toggle",
  "restore_files",
  "backup_create",
  "backup_create_busy",
  "backup_restore",
  "backup_boost",
  "file_save",
  "truck_sim_packages_upload",
  "conan_ue5_migration",
  "conan_ue5_migration_reset",
  "force_update",
  "txadmin_password_reset",
  "ram_purchase",
  "ram_deactivate",
  // Save tools
  "dragonwilds_backup_restore",
  "dragonwilds_import",
  "dragonwilds_world_settings",
  "enshrouded_backup_restore",
  "enshrouded_import",
  "palworld_backup_restore",
  "palworld_import",
  "scum_backup_restore",
  "scum_import",
  "valheim_backup_restore",
  "valheim_import",
  "zomboid_backup_restore",
  "zomboid_import",
  "zomboid_world_mods",
] as const;

export type CtxLoadTrigger = (typeof CTX_LOAD_TRIGGERS)[number];

/**
 * Automatic reloads that no customer is waiting for. A hidden tab defers them until it is shown again,
 * then runs one load for all of them.
 */
export const DEFERRABLE_CTX_LOAD_TRIGGERS: ReadonlySet<CtxLoadTrigger> = new Set<CtxLoadTrigger>([
  "transition_end",
  "event_backup_user",
  "event_backup_auto",
  "event_backup_other",
]);

/** The trigger for a `backup_created` service event, by its payload type */
export function backupEventTrigger(type: string | null | undefined): CtxLoadTrigger {
  if (type === "user") return "event_backup_user";
  if (type === "user_auto") return "event_backup_auto";
  return "event_backup_other";
}

/**
 * Query parameters that tag a ctx request. `merged` lists the triggers that were coalesced or deferred
 * into this load, joined with "." because URLSearchParams would encode "," as "%2C".
 */
export function ctxLoadUrlParams({
  trigger,
  merged = [],
  hidden,
}: {
  trigger: CtxLoadTrigger;
  merged?: ReadonlyArray<CtxLoadTrigger>;
  hidden: boolean;
}): Record<string, string> {
  const params: Record<string, string> = { trigger, vis: hidden ? "hidden" : "visible" };
  if (merged.length) params.merged = merged.join(".");
  return params;
}

/**
 * The trigger of a load started by GameServer.vue's route watcher. "auth" only for a ctx that was already
 * loaded: the first load often waits for Keycloak, so it also arrives as an authentication change.
 */
export function routeLoadTrigger({
  authStateChanged,
  ctxLoaded,
}: {
  authStateChanged: boolean;
  ctxLoaded: boolean;
}): CtxLoadTrigger {
  return authStateChanged && ctxLoaded ? "auth" : "route";
}
